import axios from 'axios';

import i18n from '@/i18n';
import { checkUserPermissions, PermissionsTypes } from '@/utils/permissions';
import {
  CLEAR_ONBOARDING,
  CLEAR_ONBOARDING_SUCCESS,
  FETCH_ONBOARDING,
  FETCH_ONBOARDING_SUCCESS,
  INSURANCE_DETAILS_REQUEST,
  ONBOARDING_DETAILS_SUCCESS,
  SAVE_INSURANCE_CONFIG,
  UPDATE_INSURANCE_STORAGE,
  UPDATE_WHATSAPP_PAID_PROMO_SHOWN,
} from '@/store/actions/onboarding';
import { USER_SUCCESS } from '@/store/actions/user';

const getHubStatus = (rootGetters) => {
  const user = rootGetters.user || rootGetters;
  const sub = user?.subscriptions;
  const { providerType } = user;
  const { permissions } = rootGetters;
  const isSuperAdmin = rootGetters.isUserSuperAdmin;

  const isServiceHubEnabled = user?.subscriptionEnabled
    ? sub?.service_hub?.status === 'active'
    : (providerType === 'service_hub' || providerType === 'default');

  const isSalesHubEnabled = user?.subscriptionEnabled
    ? sub?.sales_hub?.status === 'active'
    : (providerType === 'sales_hub' || providerType === 'default');

  const hasServiceHubAccess = isSuperAdmin || !permissions
    || checkUserPermissions([PermissionsTypes.POLICIES_VIEW], permissions);
  const hasSalesHubAccess = isSuperAdmin || !permissions
    || checkUserPermissions([PermissionsTypes.LEADS_VIEW], permissions);

  return {
    isServiceHubEnabled: isServiceHubEnabled && hasServiceHubAccess,
    isSalesHubEnabled: isSalesHubEnabled && hasSalesHubAccess,
    bothHubsEnabled: isServiceHubEnabled && hasServiceHubAccess && isSalesHubEnabled && hasSalesHubAccess,
  };
};

const state = {
  // firstHub is stored in localStorage — no server round-trip needed.
  // Values: 'sales' | 'service' | null (null = hub not chosen yet → show hub selection modal)
  firstHub: localStorage.getItem('onboarding_first_hub') || null,
  // currentHub tracks which hub is currently being onboarded (advances after each hub is completed)
  currentHub: localStorage.getItem('onboarding_current_hub') || localStorage.getItem('onboarding_first_hub') || null,
  onboardingInsuranceCompany: null,
  onboardingLoading: true,
  insuranceConfig: null,
  needsRedirect: null,
  whatsAppPaidPromoShown: false,
  isOnboardingSimulated: !!localStorage.getItem('onboarding_simulate'),
  // Simulation sub-flags: track when a lead/policy is created during simulation
  // so the skeleton advances to the next hub's step.
  simulatedLeadCreated: !!localStorage.getItem('onboarding_simulate_lead_created'),
  simulatedPolicyCreated: !!localStorage.getItem('onboarding_simulate_policy_created'),
};

const getters = {
  firstHub: (s) => s.firstHub,
  currentHub: (s) => s.currentHub,
  insuranceConfig: (s) => s.insuranceConfig,
  onboardingInsuranceCompany: (s) => s.onboardingInsuranceCompany,
  onboardingLoading: (s) => s.onboardingLoading,
  needsRedirect: (s) => s.needsRedirect,
  whatsAppPaidPromoShown: (s) => s.whatsAppPaidPromoShown,
  isOnboardingSimulated: (s) => s.isOnboardingSimulated,
  simulatedLeadCreated: (s) => s.simulatedLeadCreated,
  simulatedPolicyCreated: (s) => s.simulatedPolicyCreated,
  // Compat getter — components can read firstHub via onboarding.firstHub
  onboarding: (s) => ({ firstHub: s.firstHub }),
  // Data-driven hub onboarding status — used by placeholders and highlighted buttons.
  // Shows when the hub is active (purchased) but has no data yet.
  // Both getters require a *known* zero. An unknown count — the request has not landed
  // yet, or it failed — must never activate the placeholder: it hides the real data and
  // applies pointer-events: none to the page, so every click becomes a silent no-op.
  isSalesHubOnboardingActive: (s, g, rs, rootGetters) => {
    const { isSalesHubEnabled } = getHubStatus(rootGetters);
    if (!isSalesHubEnabled) return false;
    if (s.isOnboardingSimulated && !s.simulatedLeadCreated) return true;
    if (rootGetters.leadTotalCountFailed) return false;
    return rootGetters.leadTotalCount === 0;
  },
  isServiceHubOnboardingActive: (s, g, rs, rootGetters) => {
    const { isServiceHubEnabled } = getHubStatus(rootGetters);
    if (!isServiceHubEnabled) return false;
    if (s.isOnboardingSimulated && !s.simulatedPolicyCreated) return true;
    if (rootGetters.allPoliciesTotalCountFailed) return false;
    return rootGetters.allPoliciesTotalCount === 0;
  },
};

const actions = {
  checkAndCompleteSimulation({ state, commit, rootGetters }) {
    if (!state.isOnboardingSimulated) return;
    const { isSalesHubEnabled, isServiceHubEnabled } = getHubStatus(rootGetters);
    const salesDone = !isSalesHubEnabled || state.simulatedLeadCreated;
    const serviceDone = !isServiceHubEnabled || state.simulatedPolicyCreated;
    if (salesDone && serviceDone) {
      commit('SET_ONBOARDING_SIMULATE', false);
    }
  },
  [INSURANCE_DETAILS_REQUEST]({ commit }, insuranceCompanyId) {
    return axios
      .post(
        '/insurance/config/detail',
        { insuranceCompanyId },
        { headers: { 'X-Locale': i18n.locale } }
      )
      .then((resp) => {
        commit(ONBOARDING_DETAILS_SUCCESS, resp.data);
        return resp.data;
      })
      .catch((err) => Promise.reject(err.response.data));
  },
  [UPDATE_INSURANCE_STORAGE]({ commit, state }, storageUpdate) {
    return axios
      .post('/insurance/config/save', {
        insuranceCompanyId: state.onboardingInsuranceCompany.insuranceCompanyId,
        storageSave: {
          ...state.onboardingInsuranceCompany.storage,
          ...storageUpdate,
        },
      })
      .then((resp) => {
        commit(ONBOARDING_DETAILS_SUCCESS, resp.data);
      });
  },
  [SAVE_INSURANCE_CONFIG]({ commit }, requestBody) {
    return axios.post('/insurance/config/save', {
      insuranceCompanyId: state.onboardingInsuranceCompany.insuranceCompanyId,
      ...requestBody,
    });
  },
  [UPDATE_WHATSAPP_PAID_PROMO_SHOWN]({ dispatch }, value) {
    dispatch(UPDATE_INSURANCE_STORAGE, { whatsAppPaidPromoShown: value });
  },
  async [FETCH_ONBOARDING]({ commit, state }) {
    let resp;
    try {
      resp = await axios.get('/user/info');
    } catch (err) {
      return Promise.reject(err.response?.data || err);
    }

    commit(FETCH_ONBOARDING_SUCCESS, resp.data);

    return resp;
  },
  [CLEAR_ONBOARDING]({ commit }) {
    commit('CLEAR_FIRST_HUB');
    commit(CLEAR_ONBOARDING_SUCCESS);
  },
};

const mutations = {
  [ONBOARDING_DETAILS_SUCCESS](state, insuranceConfig) {
    state.onboardingInsuranceCompany = insuranceConfig;
    state.insuranceConfig = insuranceConfig;
    state.whatsAppPaidPromoShown = !!insuranceConfig?.storage?.whatsAppPaidPromoShown;
  },
  [FETCH_ONBOARDING_SUCCESS](state) {
    state.onboardingLoading = false;
  },
  [USER_SUCCESS]() {},
  [CLEAR_ONBOARDING_SUCCESS]() {},
  SET_FIRST_HUB(state, value) {
    state.firstHub = value;
    if (value) {
      localStorage.setItem('onboarding_first_hub', value);
    } else {
      localStorage.removeItem('onboarding_first_hub');
    }
  },
  CLEAR_FIRST_HUB(state) {
    state.firstHub = null;
    localStorage.removeItem('onboarding_first_hub');
  },
  SET_NEEDS_REDIRECT(state, path) {
    state.needsRedirect = path;
  },
  CLEAR_NEEDS_REDIRECT(state) {
    state.needsRedirect = null;
  },
  [UPDATE_WHATSAPP_PAID_PROMO_SHOWN](state, value) {
    state.whatsAppPaidPromoShown = value;
  },
  SET_ONBOARDING_SIMULATE(state, value) {
    state.isOnboardingSimulated = value;
    if (value) {
      localStorage.setItem('onboarding_simulate', '1');
    } else {
      localStorage.removeItem('onboarding_simulate');
      localStorage.removeItem('onboarding_first_hub');
      state.simulatedLeadCreated = false;
      state.firstHub = null;
      localStorage.removeItem('onboarding_simulate_lead_created');
      state.simulatedPolicyCreated = false;
      localStorage.removeItem('onboarding_simulate_policy_created');
    }
  },
  SET_SIMULATED_LEAD_CREATED(state, value) {
    state.simulatedLeadCreated = value;
    if (value) localStorage.setItem('onboarding_simulate_lead_created', '1');
    else localStorage.removeItem('onboarding_simulate_lead_created');
  },
  SET_SIMULATED_POLICY_CREATED(state, value) {
    state.simulatedPolicyCreated = value;
    if (value) localStorage.setItem('onboarding_simulate_policy_created', '1');
    else localStorage.removeItem('onboarding_simulate_policy_created');
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
